import * as THREE from "three";

interface CanvasOptions {
  particleColor?: string;
  particleCount?: number;
  particleSize?: number;
}

export const useLandingCanvas = (options: CanvasOptions = {}) => {
  const {
    particleColor = "#8b5cf6", // Premium violet default to match high-end UI
    particleCount = 1500,
    particleSize = 0.03,
  } = options;

  let scene: THREE.Scene | null = null;
  let camera: THREE.PerspectiveCamera | null = null;
  let renderer: THREE.WebGLRenderer | null = null;
  let particles: THREE.Points | null = null;
  let animationFrameId: number | null = null;

  // Track mouse coordinates for the parallax interaction
  const mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };

  const init = (canvasElement: HTMLCanvasElement) => {
    if (!canvasElement) return;

    // 1. Scene & Camera Setup
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(
      75,
      canvasElement.clientWidth / canvasElement.clientHeight,
      0.1,
      100,
    );
    camera.position.z = 5;

    // 2. Renderer Setup
    renderer = new THREE.WebGLRenderer({
      canvas: canvasElement,
      alpha: true, // Crucial for letting CSS background gradients show through
      antialias: true,
    });
    renderer.setSize(
      canvasElement.clientWidth,
      canvasElement.clientHeight,
      false,
    );
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // 3. Geometry: Creating the Particle Field
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i += 3) {
      // Distribute particles randomly in a 3D box space
      positions[i] = (Math.random() - 0.5) * 10; // X
      positions[i + 1] = (Math.random() - 0.5) * 10; // Y
      positions[i + 2] = (Math.random() - 0.5) * 10; // Z
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    // 4. Material: Customizing the Particles
    // We use a basic canvas circle texture emulation using a fragment shader trick for perfectly soft round particles
    const material = new THREE.PointsMaterial({
      color: new THREE.Color(particleColor),
      size: particleSize,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending, // Makes overlapping particles glow brighter
      depthWrite: false,
    });

    particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // 5. Event Listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    // 6. Start the Loop
    animate();
  };

  const handleMouseMove = (event: MouseEvent) => {
    // Normalize mouse coordinates between -1 and 1
    mouse.targetX = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.targetY = -(event.clientY / window.innerHeight) * 2 + 1;
  };

  const handleResize = () => {
    if (!renderer || !camera || !renderer.domElement) return;
    const width =
      renderer.domElement.parentElement?.clientWidth || window.innerWidth;
    const height =
      renderer.domElement.parentElement?.clientHeight || window.innerHeight;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };

  const animate = () => {
    if (!scene || !camera || !renderer || !particles) return;

    animationFrameId = requestAnimationFrame(animate);

    // Smooth easing/lerp for the mouse movement
    mouse.x += (mouse.targetX - mouse.x) * 0.05;
    mouse.y += (mouse.targetY - mouse.y) * 0.05;

    // Idle floating rotation + reactive mouse tracking
    particles.rotation.y = performance.now() * 0.00005 + mouse.x * 0.2;
    particles.rotation.x = performance.now() * 0.00002 + mouse.y * 0.2;

    renderer.render(scene, camera);
  };

  const cleanup = () => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("mousemove", handleMouseMove);

    // Dispose geometries and materials to avoid memory leaks
    if (particles) {
      particles.geometry.dispose();
      if (Array.isArray(particles.material)) {
        particles.material.forEach((m) => m.dispose());
      } else {
        particles.material.dispose();
      }
    }
    if (renderer) renderer.dispose();
  };

  return {
    init,
    cleanup,
  };
};
